import { useState } from 'react';

import {
  DEFAULT_TRAFFIC_FILTERS,
  useProxyVaultExchange,
  useProxyVaultTraffic,
  type TrafficFilters,
} from '../../hooks/use-proxy-vault';

import {
  Empty,
  OperationGlyphs,
  fmtBytes,
  fmtWhen,
  inputStyle,
  mono,
  selectStyle,
} from './ui';

import type { ProxyVaultExchange } from '../../lib/api-client';

/**
 * The traffic view — Charles-proxy shaped, and safe to show in full.
 *
 * The reason this can exist at all is the capture rule on the server: only the
 * CELL-SIDE form is ever stored — the request before reveal, the response after
 * redact. What an operator reads here is exactly what the LLM saw: stand-ins
 * everywhere, real values nowhere. Not redacted for display; never captured.
 *
 * Three row kinds, matching the three storage rules. HTTP text rows open a full
 * request/response. Binary rows carry a content type and a size, never the
 * blob. TCP lanes are session records — a bidirectional stream has no
 * request/response to show, so byte counts are the honest unit.
 */
export function TrafficPane({ connectionId }: { connectionId?: string }) {
  const [filters, setFilters] = useState<TrafficFilters>(DEFAULT_TRAFFIC_FILTERS);
  const [live, setLive] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const { exchanges, status } = useProxyVaultTraffic(filters, {
    ...(connectionId !== undefined && { connectionId }),
    live,
  });
  const detail = useProxyVaultExchange(selected);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 20px',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <input
          placeholder="host…"
          value={filters.host}
          data-testid="pv-traffic-host"
          onChange={(e) => setFilters({ ...filters, host: e.target.value })}
          style={{ ...inputStyle, width: 180 }}
        />
        <select
          value={filters.kind}
          onChange={(e) => setFilters({ ...filters, kind: e.target.value as TrafficFilters['kind'] })}
          style={selectStyle}
        >
          <option value="all">All kinds</option>
          <option value="http">HTTP</option>
          <option value="binary">Binary</option>
          <option value="tcp">TCP lanes</option>
        </select>
        {!connectionId && (
          <select
            value={filters.connection}
            onChange={(e) =>
              setFilters({ ...filters, connection: e.target.value as TrafficFilters['connection'] })
            }
            style={selectStyle}
            data-testid="pv-traffic-connection"
          >
            <option value="all">Matched and passthrough</option>
            <option value="matched">Matched a connection</option>
            <option value="passthrough">Passthrough</option>
          </select>
        )}
        <select
          value={filters.verdict}
          onChange={(e) =>
            setFilters({ ...filters, verdict: e.target.value as TrafficFilters['verdict'] })
          }
          style={selectStyle}
        >
          <option value="all">All outcomes</option>
          <option value="forwarded">Forwarded</option>
          <option value="refused">Refused</option>
        </select>
        <select
          value={String(filters.sinceHours)}
          onChange={(e) => setFilters({ ...filters, sinceHours: Number(e.target.value) })}
          style={selectStyle}
        >
          <option value="1">Last hour</option>
          <option value="24">Last 24h</option>
          <option value="168">Last 7 days</option>
        </select>
        <label
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            color: 'var(--text-soft)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={live}
            data-testid="pv-traffic-live"
            onChange={(e) => setLive(e.target.checked)}
          />
          ⟳ live tail
        </label>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <div style={{ flex: '1 1 55%', overflowY: 'auto', borderRight: '1px solid var(--border)' }}>
          {status === 'loading' && exchanges.length === 0 && <Empty>Loading…</Empty>}
          {status === 'success' && exchanges.length === 0 && (
            <Empty>
              Nothing has flowed through the proxy in this window. Passthrough traffic appears here
              too, so an empty list usually means no cell has run.
            </Empty>
          )}
          {exchanges.map((exchange) => (
            <ExchangeRow
              key={exchange.id}
              exchange={exchange}
              selected={selected === exchange.id}
              onSelect={() => setSelected(exchange.id)}
            />
          ))}
        </div>

        <div style={{ flex: '1 1 45%', overflowY: 'auto', padding: 16 }}>
          {!detail && (
            <div style={{ fontSize: 12, color: 'var(--text-mute)' }}>
              Select an exchange to see it exactly as the cell did.
            </div>
          )}
          {detail && <ExchangeDetail exchange={detail} />}
        </div>
      </div>
    </div>
  );
}

function ExchangeRow({
  exchange,
  selected,
  onSelect,
}: {
  exchange: ProxyVaultExchange;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      data-testid="pv-exchange-row"
      style={{
        padding: '8px 20px',
        borderBottom: '1px solid var(--border-soft)',
        cursor: 'pointer',
        background: selected ? 'var(--bg-alt)' : 'transparent',
        fontSize: 12,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <span style={{ fontFamily: mono, fontSize: 11, color: 'var(--text-mute)' }}>
          {fmtWhen(exchange.created_at)}
        </span>
        <span style={{ fontWeight: 500 }}>
          {exchange.kind === 'tcp' ? 'TCP' : (exchange.method ?? '—')}
        </span>
        <span style={{ fontFamily: mono, color: 'var(--text-soft)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {exchange.host}
          {exchange.path ?? ''}
        </span>
        <span style={{ color: exchange.verdict === 'refused' ? '#c0392b' : 'var(--text-soft)' }}>
          {exchange.status ?? (exchange.verdict === 'refused' ? 'refused' : '—')}
        </span>
        <OperationGlyphs operations={exchange.operations} verdict={exchange.verdict} />
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-mute)', marginTop: 2 }}>
        {exchange.connection_id ? 'matched' : 'passthrough'}
        {exchange.kind === 'binary' && ` · ⬒ ${exchange.content_type ?? 'binary'} ${fmtBytes(exchange.bytes_down)}`}
        {exchange.kind === 'tcp' &&
          ` · lane · ${fmtBytes(exchange.bytes_up)}↑ ${fmtBytes(exchange.bytes_down)}↓`}
        {exchange.error_code && ` · ${exchange.error_code}`}
        {exchange.duration_ms !== null && ` · ${exchange.duration_ms}ms`}
      </div>
    </div>
  );
}

function ExchangeDetail({
  exchange,
}: {
  exchange: {
    method: string | null;
    host: string;
    path: string | null;
    kind: string;
    status: number | null;
    duration_ms: number | null;
    connection_id: string | null;
    tenant_id: string;
    user_id: string | null;
    claim_id: string | null;
    error_code: string | null;
    trace_id: string | null;
    truncated: boolean;
    content_type: string | null;
    bytes_up: number | null;
    bytes_down: number | null;
    operations: Array<{ verb: string; target: string; where: string }>;
    request_headers: Record<string, unknown>;
    request_body: string | null;
    response_headers: Record<string, unknown>;
    response_body: string | null;
  };
}) {
  return (
    <div style={{ fontSize: 12 }} data-testid="pv-exchange-detail">
      <div style={{ fontWeight: 600, marginBottom: 2 }}>
        {exchange.method ?? exchange.kind.toUpperCase()} {exchange.host}
        {exchange.path ?? ''}
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-mute)', marginBottom: 12, fontFamily: mono }}>
        org {exchange.tenant_id}
        {exchange.user_id && ` · user ${exchange.user_id}`}
        {exchange.claim_id && ` · claim ${exchange.claim_id}`}
        {exchange.connection_id ? ` · conn ${exchange.connection_id}` : ' · passthrough'}
      </div>

      {exchange.operations.length > 0 && (
        <Section title="Operations">
          {exchange.operations.map((op, i) => (
            <div key={i} style={{ fontFamily: mono, fontSize: 11 }}>
              {op.verb === 'reveal' ? '▲' : op.verb === 'redact' ? '▼' : '✎'} {op.verb} {op.target} →{' '}
              {op.where}
            </div>
          ))}
        </Section>
      )}

      {exchange.error_code && (
        <Section title="Refused">
          <div style={{ fontFamily: mono, fontSize: 11, color: '#c0392b' }}>{exchange.error_code}</div>
        </Section>
      )}

      {exchange.kind === 'binary' && (
        <Section title="Binary body">
          <div style={{ color: 'var(--text-soft)' }}>
            {exchange.content_type ?? 'unknown type'} · {fmtBytes(exchange.bytes_down)} — recorded as
            a row, never as a blob.
          </div>
        </Section>
      )}

      {exchange.kind === 'tcp' && (
        <Section title="TCP session">
          <div style={{ color: 'var(--text-soft)' }}>
            {fmtBytes(exchange.bytes_up)} up · {fmtBytes(exchange.bytes_down)} down ·{' '}
            {exchange.duration_ms ?? '—'}ms. A bidirectional stream has no request and response to
            store, so the session record is the honest unit.
          </div>
        </Section>
      )}

      {exchange.kind === 'http' && (
        <>
          <Section title="Request — as the cell sent it">
            <Headers headers={exchange.request_headers} />
            <Body text={exchange.request_body} />
          </Section>
          <Section title="Response — as the cell saw it">
            <Headers headers={exchange.response_headers} />
            <Body text={exchange.response_body} />
          </Section>
        </>
      )}

      {exchange.truncated && (
        <div style={{ fontSize: 10, color: 'var(--text-mute)' }}>
          A body hit the capture cap and was cut.
        </div>
      )}
      {exchange.trace_id && (
        <div style={{ fontSize: 10, color: 'var(--text-mute)', fontFamily: mono, marginTop: 8 }}>
          trace {exchange.trace_id}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'var(--text-mute)',
          marginBottom: 4,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Headers({ headers }: { headers: Record<string, unknown> }) {
  const entries = Object.entries(headers ?? {});
  if (entries.length === 0) return null;
  return (
    <pre
      style={{
        fontFamily: mono,
        fontSize: 10.5,
        color: 'var(--text-soft)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        margin: '0 0 6px',
      }}
    >
      {entries.map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`).join('\n')}
    </pre>
  );
}

function Body({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <pre
      style={{
        fontFamily: mono,
        fontSize: 10.5,
        background: 'var(--bg-alt)',
        padding: 8,
        borderRadius: 6,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        maxHeight: 260,
        overflow: 'auto',
        margin: 0,
      }}
    >
      {text}
    </pre>
  );
}
